import { Queue } from '@datastructures-js/queue'
import _ from 'lodash'
import { createContext, PropsWithChildren, useContext, useMemo } from 'react'

import { FormulaConditionJson, FormulaJson, FormulaValueJson } from '@doss/common-all'

export interface FormulaContextValue {
  formula: FormulaJson
  columnChips: FormulaValueJson[]
  tableId: string
}

export const FormulaContext = createContext<FormulaContextValue | null>(null)

/**
 * Hook to get the formula context
 */
export function useFormulaContext() {
  const context = useContext(FormulaContext)
  if (!context) {
    throw new Error('useFormulaContext must be used within a FormulaProvider')
  }
  return context
}

/**
 * Provides the formula context to the children
 */
export function FormulaProvider({
  formula,
  tableId,
  children,
}: PropsWithChildren<{ formula: FormulaJson; tableId: string }>) {
  const formulaColumnChips = useMemo(() => {
    const columnChips: FormulaValueJson[] = []
    const queue = new Queue<FormulaJson | FormulaValueJson | FormulaConditionJson>()

    queue.push(formula)

    while (!queue.isEmpty()) {
      const node = queue.dequeue()

      // If the node is a formula value containing a table column, add it to the column chips
      if ('tableColumn' in node && !_.isNil(node.tableColumn)) {
        columnChips.push(node)
        continue
      }

      // If the node is a formula value, add the items it contains to the queue for further processing
      if ('formulaValues' in node) {
        node.formulaValues.forEach((value) => queue.push(value))
      }

      // If the node is a formula value or formula condition, add its formula to the queue for further processing
      if ('formula' in node && !_.isNil(node.formula)) {
        queue.push(node.formula)
      }

      // If the node is a formula condition, add the items it contains to the queue for further processing
      if ('conditions' in node) {
        node.conditions.forEach((condition) => queue.push(condition))
      }
    }

    return columnChips
  }, [formula])

  const value = useMemo(
    () => ({
      formula,
      columnChips: formulaColumnChips,
      tableId,
    }),
    [formula, formulaColumnChips, tableId],
  )

  return <FormulaContext.Provider value={value}>{children}</FormulaContext.Provider>
}
